import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/constants";
import { Shield, Users2, Network, Trash2, Plus, Search, Save } from "lucide-react";

const TABS = [
  { id: "matrix", label: "Permission Matrix", icon: Shield },
  { id: "sso", label: "SSO Group Mappings", icon: Network },
  { id: "delegations", label: "Delegated Roles", icon: Users2 },
];

export default function RBACAdmin() {
  const [tab, setTab] = useState("matrix");
  return (
    <div className="p-6" data-testid="rbac-admin-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 1 · RBAC & Access</div>
        <h1 className="text-2xl font-bold text-slate-900">Access Control</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure the permission matrix, map Entra ID groups to roles, and manage delegated department admins.
        </p>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`rbac-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                tab === t.id ? "border-[#005289] text-[#005289] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "matrix" && <MatrixEditor />}
      {tab === "sso" && <SSOMappings />}
      {tab === "delegations" && <Delegations />}
    </div>
  );
}

// ---------- Permission Matrix ----------
function MatrixEditor() {
  const [catalog, setCatalog] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([api.get("/rbac/catalog"), api.get("/rbac/matrix")]);
        setCatalog(c.data);
        setRows(m.data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load matrix"); }
    })();
  }, []);

  const byKey = useMemo(() => {
    const map = {};
    for (const r of rows) map[`${r.role}|${r.module}|${r.action}`] = r;
    return map;
  }, [rows]);

  const toggle = async (role, module, action) => {
    const key = `${role}|${module}|${action}`;
    const cur = byKey[key];
    const next = !(cur?.allowed);
    setBusyKey(key);
    try {
      await api.put("/rbac/matrix", { role, module, action, allowed: next });
      setRows(prev => {
        const found = prev.find(r => r.role === role && r.module === module && r.action === action);
        if (found) return prev.map(r => r === found ? { ...r, allowed: next, source: "override" } : r);
        return [...prev, { role, module, action, allowed: next, source: "override" }];
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusyKey(null); }
  };

  if (!catalog) return <div className="text-slate-500 text-sm">Loading matrix…</div>;

  const filteredRoles = catalog.roles.filter(r => !roleFilter || r === roleFilter);
  const filteredModules = catalog.modules.filter(m => !filter || m.includes(filter.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="matrix-editor">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="matrix-filter-module"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter modules..."
            className="h-9 pl-9 pr-3 border border-slate-200 rounded-md text-sm w-64"
          />
        </div>
        <select
          data-testid="matrix-filter-role"
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="h-9 px-3 border border-slate-200 rounded-md text-sm"
        >
          <option value="">All roles</option>
          {catalog.roles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500">Click a cell to toggle. Changes save immediately + audited.</div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 border-b border-slate-200 font-semibold text-slate-600">Role</th>
              {filteredModules.map(m => (
                <th key={m} className="px-3 py-2 border-b border-slate-200 font-semibold text-slate-600" colSpan={catalog.actions.length}>
                  {m}
                </th>
              ))}
            </tr>
            <tr>
              <th className="px-3 py-1 border-b border-slate-200 text-slate-400 text-[10px]"></th>
              {filteredModules.map(m => (
                catalog.actions.map(a => (
                  <th key={`${m}-${a}`} className="px-2 py-1 border-b border-slate-200 text-[10px] text-slate-400 font-medium">
                    {a}
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRoles.map(role => (
              <tr key={role} className="hover:bg-slate-50">
                <td className="px-3 py-2 border-b border-slate-100 font-medium text-slate-800 whitespace-nowrap">
                  {ROLE_LABELS[role] || role}
                </td>
                {filteredModules.map(m => (
                  catalog.actions.map(a => {
                    const key = `${role}|${m}|${a}`;
                    const row = byKey[key];
                    const allowed = !!row?.allowed;
                    const busy = busyKey === key;
                    return (
                      <td key={key} className="px-2 py-2 border-b border-slate-100 text-center">
                        <button
                          data-testid={`cell-${role}-${m}-${a}`}
                          disabled={busy}
                          onClick={() => toggle(role, m, a)}
                          className={`w-6 h-6 rounded ${allowed ? "bg-emerald-500 hover:bg-emerald-600" : "bg-slate-200 hover:bg-slate-300"} ${busy ? "opacity-50" : ""}`}
                          title={allowed ? "Allowed" : "Denied"}
                        >
                          <span className="text-white text-xs font-bold">{allowed ? "✓" : ""}</span>
                        </button>
                      </td>
                    );
                  })
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- SSO Mappings ----------
function SSOMappings() {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [gid, setGid] = useState("");
  const [gname, setGname] = useState("");
  const [role, setRole] = useState("business_unit_rep");
  const [status, setStatus] = useState(null);

  const load = async () => {
    try {
      const [c, m, s] = await Promise.all([
        api.get("/rbac/catalog"),
        api.get("/rbac/sso/mappings"),
        api.get("/sso/status"),
      ]);
      setCatalog(c.data); setRows(m.data); setStatus(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rbac/sso/mappings", { group_id: gid.trim(), group_name: gname.trim(), role });
      setGid(""); setGname(""); toast.success("Mapping added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this mapping?")) return;
    try { await api.delete(`/rbac/sso/mappings/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="sso-mappings">
      {status && (
        <div className={`text-xs px-3 py-2 rounded-md border ${
          status.enabled ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"
        }`} data-testid="sso-status">
          <span className="font-semibold">{status.enabled ? "Entra ID: LIVE" : "Entra ID: SANDBOX MODE"}</span>
          {" — "}{status.message}
        </div>
      )}
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end bg-slate-50 border border-slate-200 rounded-md p-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Entra Group Object ID</label>
          <input data-testid="mapping-group-id" value={gid} onChange={e => setGid(e.target.value)} required placeholder="GUID"
                 className="h-9 px-3 border border-slate-200 rounded-md text-sm w-72"/>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Display Name</label>
          <input data-testid="mapping-group-name" value={gname} onChange={e => setGname(e.target.value)} placeholder="HCD Legal Team"
                 className="h-9 px-3 border border-slate-200 rounded-md text-sm w-64"/>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Role</label>
          <select data-testid="mapping-role" value={role} onChange={e => setRole(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm">
            {catalog?.roles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
          </select>
        </div>
        <button data-testid="mapping-add-btn" className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs text-slate-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2">Group ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody data-testid="mapping-list">
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">No mappings yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{r.group_id}</td>
                <td className="px-3 py-2">{r.group_name || "—"}</td>
                <td className="px-3 py-2">{ROLE_LABELS[r.role] || r.role}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`mapping-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Delegated Roles ----------
function Delegations() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("business_unit_rep");
  const [validUntil, setValidUntil] = useState("");
  const DELEGABLE = ["business_unit_rep", "department_director", "department_admin", "purchasing"];

  const load = async () => {
    try {
      const [d, u] = await Promise.all([
        api.get("/rbac/delegations"),
        api.get("/users"),
      ]);
      setRows(d.data); setUsers(u.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!uid) return toast.error("Select a user");
    try {
      await api.post("/rbac/delegations", {
        granted_to_user_id: uid, role, valid_until: validUntil || null,
      });
      toast.success("Delegation granted"); setUid(""); setValidUntil(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this delegation?")) return;
    try { await api.delete(`/rbac/delegations/${id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="delegations-panel">
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end bg-slate-50 border border-slate-200 rounded-md p-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">User</label>
          <select data-testid="delegate-user" value={uid} onChange={e => setUid(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm min-w-[280px]">
            <option value="">Select user…</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} · {u.email} · {u.department || "—"}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Delegable Role</label>
          <select data-testid="delegate-role" value={role} onChange={e => setRole(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm">
            {DELEGABLE.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Valid Until (optional)</label>
          <input data-testid="delegate-until" type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)}
                 className="h-9 px-3 border border-slate-200 rounded-md text-sm"/>
        </div>
        <button data-testid="delegate-add-btn" className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1">
          <Plus className="w-4 h-4" /> Grant
        </button>
      </form>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs text-slate-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Department</th>
              <th className="text-left px-3 py-2">Valid Until</th>
              <th className="text-left px-3 py-2">Granted By</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody data-testid="delegation-list">
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No delegations yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{r.granted_to_name}</div>
                  <div className="text-xs text-slate-500">{r.granted_to_email}</div>
                </td>
                <td className="px-3 py-2">{ROLE_LABELS[r.role] || r.role}</td>
                <td className="px-3 py-2">{r.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.valid_until || "No expiry"}</td>
                <td className="px-3 py-2 text-xs">{r.granted_by || "—"}</td>
                <td className="px-3 py-2">
                  {r.revoked_at ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Revoked</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {!r.revoked_at && (
                    <button data-testid={`delegate-revoke-${r.id}`} onClick={() => revoke(r.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
